<script setup lang="ts">
/**
 * ApplicationStatusSelect
 *
 * Housing-module form element that renders a dropdown of allowed application
 * statuses. Computes which statuses are enabled/disabled using housing context
 * from sharedAnswers (statusOnCreate), without any API call.
 *
 * Data flow:
 *  1. Read housing:statusOnCreate from sharedAnswers.
 *  2. Compute allowed statuses with threshold logic (statuses beyond the
 *     effective status are disabled). Locked / Offer Accepted applications
 *     are resume-only: the sole option is the status they started with
 *     (see RESUME_ONLY in computeAllowedStatuses).
 *  3. Keep the saved answer aligned with the allowed set: a stale answer
 *     saved under older rules is cleared (the backend applies whatever answer
 *     is stored), and a resume-only single option is selected automatically.
 */
import { inject, computed, watch } from "vue";
import type { FormElementBase, ApplicationStatusSelectProperties } from "~/api";
import type { DefaultFormComponentProps } from "./FormComponentRenderer.vue";
import {
  formResponseInjectKey,
  type FormResponseState,
} from "../../composables/useFormResponse";
import { computeAllowedStatuses } from "~/YYC_Housing/utils/computeAllowedStatuses";

const props = defineProps<DefaultFormComponentProps<FormElementBase>>();

const formState = inject(formResponseInjectKey) as FormResponseState;
const answer = formState.useAnswer(props.modelValue);

// ---------------------------------------------------------------------------
// Status computation from sharedAnswers (no API call)
// ---------------------------------------------------------------------------

const statusOnCreate = computed(
  () => formState.sharedAnswers?.["housing:statusOnCreate"] ?? null,
);

const items = computed(() => computeAllowedStatuses(statusOnCreate.value));

// The saved answer must stay within the allowed set — the backend applies
// whatever answer is stored when the workflow resolves, so a stale value
// saved under older rules (e.g. "InReview" from before the pre-hold status
// restricted the options, #1010) would silently bypass them. Clear invalid
// answers, and when only one status is allowed (resume-only), select it.
watch(
  [items, answer],
  () => {
    if (props.isReadOnly) return;
    const enabled = items.value.filter((i) => !i.disabled);
    const current = answer.value ?? null;
    const isValid =
      current !== null && enabled.some((i) => i.value === current);
    if (isValid) return;

    if (enabled.length === 1) {
      answer.value = enabled[0].value;
    } else if (current !== null) {
      answer.value = null;
    }
  },
  { immediate: true },
);

const elementProperties = computed(
  () =>
    (props.modelValue.properties ?? {}) as ApplicationStatusSelectProperties,
);
</script>

<template>
  <div class="ApplicationStatusSelectComponent">
    <slot name="label" :element="modelValue" />

    <div style="flex-grow: 1"></div>

    <el-select
      :model-value="answer ?? undefined"
      :placeholder="
        elementProperties.placeholder ?? 'Select application status'
      "
      :disabled="isReadOnly"
      @update:model-value="answer = $event ?? null"
    >
      <el-option
        v-for="item in items"
        :key="item.value"
        :label="item.label"
        :value="item.value"
        :disabled="item.disabled"
      />
    </el-select>
  </div>
</template>

<style scoped lang="less">
.ApplicationStatusSelectComponent {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
}
</style>
